import { useMemo } from 'react';

import { useItinerary } from '@/handlers/useItinerary';
import { StyledFlexColumn } from '@/layout/FlexLayout';
import { formatDateDMY } from '@/utils/formatDate/formatDate';
import { formatPrice } from '@/utils/formatPrice/formatPrice';
import { getHeaderImage } from '@/utils/getImage/';

import { StyledImage, StyledPersonIndicator, StyledRow } from './styles';

import { CardWithHeader } from '../CardWithHeader';
import { Paragraph } from '../Paragraph';
import { Spacer } from '../Spacer';

interface OverviewCardProps {
  secondary?: boolean;
}

export function OverviewCard({ secondary }: OverviewCardProps) {
  const { data } = useItinerary();

  const dateFromToText = useMemo(() => {
    const startDate = data?.startDate || '';
    const endDate = data?.endDate || '';
    return startDate && endDate ? `${formatDateDMY(startDate)} to ${formatDateDMY(endDate)}` : '';
  }, [data?.startDate, data?.endDate]);

  const destination = useMemo(() => data?.Destination.name || 'N/A', [data?.Destination]);
  const formattedPrice = useMemo(() => (data?.price ? formatPrice(data.price) : 'N/A'), [data?.price]);
  const formattedPriceAdults = useMemo(
    () => (data?.priceAdults ? formatPrice(data.priceAdults) : 'N/A'),
    [data?.priceAdults],
  );
  const formattedPriceKids = useMemo(() => (data?.priceKids ? formatPrice(data.priceKids) : 'N/A'), [data?.priceKids]);
  const formattedPriceBabies = useMemo(
    () => (data?.priceAdults && data?.priceKids ? formatPrice(data.priceAdults - data.priceKids) : 'N/A'),
    [data?.priceAdults, data?.priceKids],
  );
  const formattedDownPayment = useMemo(() => (data?.price ? formatPrice(data.price * 0.25) : 'N/A'), [data?.price]);
  const formattedRestPayment = useMemo(() => (data?.price ? formatPrice(data.price * 0.75) : 'N/A'), [data?.price]);

  const mainContent = useMemo(
    () => data?.Destination.Content.find((content) => content.type.toLowerCase() === 'main'),
    [data?.Destination.Content],
  );
  const mainHeaderImage = useMemo(() => (mainContent ? getHeaderImage(mainContent) : ''), [mainContent]);

  if (!data) return null;
  return (
    <CardWithHeader
      title={secondary ? 'Price Overview' : destination}
      subtitle={secondary ? '' : dateFromToText}
      flex="1"
    >
      {secondary && (
        <StyledFlexColumn>
          <StyledImage src={mainHeaderImage} alt="destination image" />
          <Spacer size="sm" />
          <Paragraph color="bodyTextColor" weight={600} size="2xl">
            {destination}
          </Paragraph>
          <Paragraph color="tertiary" size="md">
            {dateFromToText}
          </Paragraph>
          <Spacer size="lg" />
        </StyledFlexColumn>
      )}

      <StyledRow>
        <StyledPersonIndicator>
          <Paragraph color="bodyTextColor" weight={600} size="md" noWrap>
            {data.amountOfAdults} {data.amountOfAdults > 1 ? 'adults' : 'adult'}
          </Paragraph>
          <Paragraph color="tertiary" size="sm" noWrap>
            (18+ yrs.)
          </Paragraph>
        </StyledPersonIndicator>
        <Paragraph color="bodyTextColor" weight={600} size="md">
          {formattedPriceAdults}
        </Paragraph>
      </StyledRow>

      {!!data.amountOfKids && (
        <StyledRow>
          <StyledPersonIndicator>
            <Paragraph color="bodyTextColor" weight={600} size="md" noWrap>
              {data.amountOfKids} {data.amountOfKids > 1 ? 'children' : 'child'}
            </Paragraph>
            <Paragraph color="tertiary" size="sm" noWrap>
              (2-18 yrs.)
            </Paragraph>
          </StyledPersonIndicator>
          <Paragraph color="bodyTextColor" weight={600} size="md">
            {formattedPriceKids}
          </Paragraph>
        </StyledRow>
      )}

      {!!data.amountOfBabies && (
        <StyledRow>
          <StyledPersonIndicator>
            <Paragraph color="bodyTextColor" weight={600} size="md" noWrap>
              {data.amountOfBabies} {data.amountOfBabies > 1 ? 'babies' : 'baby'}
            </Paragraph>
            <Paragraph color="tertiary" size="sm" noWrap>
              (0-1 yrs.)
            </Paragraph>
          </StyledPersonIndicator>
          <Paragraph color="bodyTextColor" weight={600} size="md">
            {formattedPriceBabies}
          </Paragraph>
        </StyledRow>
      )}

      <Spacer size="md" />

      <StyledRow $noBorder>
        <Paragraph color="bodyTextColor" weight={600} size="2xl">
          Total
        </Paragraph>
        <Paragraph color="bodyTextColor" weight={600} size="2xl">
          {formattedPrice}
        </Paragraph>
      </StyledRow>

      <StyledRow $noBorder $smallPadding>
        <Paragraph color="bodyTextColor" weight={600} size="md" noWrap>
          Down payment 25%
        </Paragraph>
        <Paragraph color="bodyTextColor" weight={600} size="md">
          {formattedDownPayment}
        </Paragraph>
      </StyledRow>

      <StyledRow $noBorder $smallPadding>
        <Paragraph color="bodyTextColor" weight={600} size="md" noWrap>
          Remaining payment 75%
        </Paragraph>
        <Paragraph color="bodyTextColor" weight={600} size="md">
          {formattedRestPayment}
        </Paragraph>
      </StyledRow>
    </CardWithHeader>
  );
}
